// PeriodChips — the one period selector (SWEEP-SCOPE §6 "Period selector"):
// Today · Yesterday · This week · This month · This quarter · This year · All time, and
// "Dates…" where a screen needs an exact window.
//
// Item 70 / item 82: the Trades log, Money movements, the Statement, Profit,
// Inventory and Trading each had their own chip row with its own words and
// order. One vocabulary, one chip (FilterRail's), one way of turning a chip
// into the two Dubai calendar days a route wants.
//
// THE RULE IT ENFORCES (§6 "Period selector", §7 rule 7): a period is a span
// of the DESK'S days — "7d" is the last seven Dubai days, "This month" starts
// on the first of the Dubai month — never the viewer's. `periodDays` goes
// through `dubaiDate`, the one lawful clock, and every screen asks it rather
// than counting days itself. "Dates…" reveals the two date boxes ON THE SAME
// ROW (19 Sept 2026 — they used to stack beneath and gave a filter bar a
// second storey); nothing else on the row explains itself.
//
// Usage:
//   const [period, setPeriod] = useState<PeriodValue>({ key: 'today' });
//   <PeriodChips value={period} onChange={setPeriod} />
//   const { from, to } = periodDays(period);     // Dubai days, both inclusive
//   api.movements({ from, to, … })
//
//   <PeriodChips value={period} onChange={setPeriod} allTime />   // adds "All time"

import { Chip } from './FilterRail';
import { dubaiDate } from '../lib/format';

export type PeriodKey = 'today' | 'yesterday' | 'week' | 'month' | 'quarter' | 'year' | 'all' | 'custom';

export interface PeriodValue {
  key: PeriodKey;
  /** Dubai calendar day, YYYY-MM-DD — read only when `key === 'custom'`. */
  from?: string;
  to?: string;
}

export const PERIOD_CHIPS: ReadonlyArray<readonly [PeriodKey, string]> = [
  ['today', 'Today'], ['yesterday', 'Yesterday'], ['week', 'This week'], ['month', 'This month'],
  ['quarter', 'This quarter'], ['year', 'This year'], ['custom', 'Dates…'],
];

const DAY_MS = 86_400_000;

/** The chip → the Dubai calendar days a route wants, both ends inclusive.
 *  An open end is `undefined` ("7d" runs to today; "All time" has no ends). */
export function periodDays(v: PeriodValue): { from?: string; to?: string } {
  const today = dubaiDate();                       // YYYY-MM-DD on the Dubai clock
  const y = Number(today.slice(0, 4)), m = Number(today.slice(5, 7));
  switch (v.key) {
    case 'today':   return { from: today, to: today };
    // The Dubai day before today (operator, 28 Oct 2026: "2-yes" — Trades opened on Today with no way to show only yesterday).
    case 'yesterday': { const d = dubaiDate(Date.parse(`${today}T00:00:00+04:00`) - DAY_MS); return { from: d, to: d }; }
    case 'week': {
      // Monday to today, on the Dubai calendar.
      const t = Date.parse(`${today}T00:00:00+04:00`);
      const dow = (new Date(t + 4 * 3_600_000).getUTCDay() + 6) % 7;   // Mon = 0
      return { from: dubaiDate(t - dow * DAY_MS) };
    }
    case 'month':   return { from: `${today.slice(0, 7)}-01` };
    case 'quarter': return { from: `${y}-${String(Math.floor((m - 1) / 3) * 3 + 1).padStart(2, '0')}-01` };
    case 'year':    return { from: `${y}-01-01` };
    case 'custom':  return { from: v.from || undefined, to: v.to || undefined };
    default:        return {};
  }
}

/** A report's period as its route takes it (`api.pnl`, the inventory read): the named periods by name; Yesterday — which
 *  the route does not name — as the two Dubai days it means, under 'custom'. */
export function reportPeriod(v: PeriodValue): { key: Exclude<PeriodKey, 'yesterday'>; from?: string; to?: string } {
  return v.key === 'custom' || v.key === 'yesterday' ? { key: 'custom', ...periodDays(v) } : { key: v.key };
}

/** A report's period as its header says it: the route's own label, but "Yesterday" for Yesterday — which goes to the route as
 *  two days, so the route would say "28 Oct 2026 to 28 Oct 2026" (P-074, operator, 29 Oct 2026). */
export function reportLabel(v: PeriodValue, routeLabel: string | undefined): string | undefined {
  return v.key === 'yesterday' ? 'Yesterday' : routeLabel;
}

/** The words for a toolbar count — "12 entries · today". */
export function periodWords(v: PeriodValue): string {
  switch (v.key) {
    case 'today':   return 'today';
    case 'yesterday': return 'yesterday';
    case 'week':    return 'this week';
    case 'month':   return 'this month';
    case 'quarter': return 'this quarter';
    case 'year':    return 'this year';
    case 'custom':  return v.from && v.to ? `${v.from} to ${v.to}` : v.from ? `from ${v.from}` : v.to ? `to ${v.to}` : 'any date';
    default:        return 'all time';
  }
}

export function PeriodChips({ value, onChange, allTime = false, showDates = true, wrap = false, className = '' }: {
  value: PeriodValue;
  onChange: (next: PeriodValue) => void;
  /** Adds an "All time" chip — for logs that show every row by default. */
  allTime?: boolean;
  /** The "Dates…" chip and its two boxes — off for a report whose route
   *  takes named periods only. */
  showDates?: boolean;
  /** On the phone the chips WRAP onto a second line instead of scrolling
   *  sideways — for a period row that stands on its own, with no filter bar
   *  under it to keep to one storey (KYT, 1 Nov 2026: a strip that scrolls
   *  hides the chips past the edge, and nothing says so). Off by default:
   *  the filter bars keep their strip. */
  wrap?: boolean;
  className?: string;
}) {
  const named = showDates ? PERIOD_CHIPS : PERIOD_CHIPS.filter(([k]) => k !== 'custom');
  const chips: ReadonlyArray<readonly [PeriodKey, string]> = allTime
    ? [...named, ['all', 'All time']]
    : named;
  return (
    // ONE ROW. The chips and — once "Dates…" is chosen — the two date boxes
    // sit on the same wrapping line, so inside a filter bar the period never
    // grows a second storey under itself (operator, 19 Sept 2026: "fix the
    // dates boxes in the filter bar"). The From/To words are the bar's own
    // label size; a date box is as wide as a date and no wider.
    <div className={`flex flex-wrap items-center gap-1.5 w-full lg:w-auto lg:gap-1 ${className}`}>
      {/* ON THE PHONE the seven chips are one strip that scrolls sideways
          (no scrollbar drawn) rather than three ragged lines; the date boxes,
          when shown, take the line beneath the strip. At lg+ the strip
          dissolves (contents) and the chips wrap with everything else. */}
      <div className={wrap
        ? 'flex flex-wrap items-center gap-1.5 max-w-full lg:contents'
        : 'flex flex-nowrap items-center gap-1.5 max-w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:contents'}>
        {chips.map(([key, label]) => (
          <Chip key={key} active={value.key === key} onClick={() => onChange({ ...value, key })}>
            {label}
          </Chip>
        ))}
      </div>
      {showDates && value.key === 'custom' && (
        <>
          {/* Dubai calendar days, both ends inclusive — the route reads them
              on the books' clock, not the browser's. */}
          <label className="ml-1 flex items-center gap-1.5 text-label uppercase tracking-[0.06em] text-tg-hint font-medium whitespace-nowrap">
            From
            <input
              type="date"
              className="input text-field lg:text-meta w-[9.5rem] normal-case tracking-normal font-normal"
              value={value.from ?? ''}
              onChange={(e) => onChange({ ...value, key: 'custom', from: e.target.value })}
            />
          </label>
          <label className="flex items-center gap-1.5 text-label uppercase tracking-[0.06em] text-tg-hint font-medium whitespace-nowrap">
            To
            <input
              type="date"
              className="input text-field lg:text-meta w-[9.5rem] normal-case tracking-normal font-normal"
              value={value.to ?? ''}
              onChange={(e) => onChange({ ...value, key: 'custom', to: e.target.value })}
            />
          </label>
        </>
      )}
    </div>
  );
}
